'use client';

import { useState } from 'react';
import Image from 'next/image';
import {
  Typography,
  Box,
  Container,
  Button,
  Stack,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { useRouter, usePathname } from 'next/navigation';
import { LocalShipping, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { TEXT, BORDER, BRAND, BACKGROUND } from '@/constants/colors';
import { brandingConfig } from '@/config/branding';

/**
 * Simple navigation bar for non-authenticated users
 * Shows minimal branding and essential links (Login, Support, Terms)
 * Mobile responsive with hamburger menu
 */
export default function SimpleNav() {
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isLoginPage = pathname === '/login/';

  const navItems = [
    { label: 'Support', path: '/support' },
    { label: 'Terms', path: '/terms' },
  ];

  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    router.push(path);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'white',
          borderBottom: `1px solid ${BORDER.light}`,
          zIndex: 1100,
        }}
      >
        {/* <Container maxWidth="lg"> */}
        <Container maxWidth="none">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {/* Logo */}
            <Box
              onClick={() => handleNavigation('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mr: 'auto',
                ml: 0,
              }}
            >
              <Image
                src="/assets/terrakite-teal-logo.png"
                alt={brandingConfig.assets.logoAlt}
                width={150}
                height={40}
                style={{ objectFit: 'contain' }}
              />
            </Box>

            {/* Desktop Navigation Links */}
            {!isMobile && (
              <Stack direction="row" spacing={2} alignItems="center">
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      textTransform: 'none',
                      color: TEXT.secondary,
                      fontWeight: 500,
                      '&:hover': {
                        color: TEXT.primary,
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                {!isLoginPage && (
                  <Button
                    variant="contained"
                    onClick={() => handleNavigation('/login')}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#000000',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.95rem',
                      '&:hover': { bgcolor: '#1a1a1a' },
                    }}
                  >
                    Login
                  </Button>
                )}
              </Stack>
            )}

            {/* Mobile Hamburger Menu */}
            {isMobile && (
              <Stack direction="row" spacing={1} alignItems="center">
                {!isLoginPage && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleNavigation('/login')}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#000000',
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      borderRadius: 2,
                      fontSize: '0.875rem',
                      '&:hover': { bgcolor: '#1a1a1a' },
                    }}
                  >
                    Login
                  </Button>
                )}
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open menu"
                  sx={{ color: '#000000' }}
                >
                  <MenuIcon />
                </IconButton>
              </Stack>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: '#ffffff',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Menu
            </Typography>
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              size="small"
              aria-label="Close menu"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)}>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
// </AppBar>
//   );
// }
